import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchTimeSeries } from "./api";
import { fitGrowth, project, type GrowthFit, type Projection } from "./scenario";
import type { ScenarioArea, ScenarioParams } from "../state/useScenarioStore";

export interface AreaScenario {
  isLoading: boolean;
  isError: boolean;
  /** The area's own fitted trend, whether or not the scenario uses it. */
  fit: GrowthFit | null;
  /** The rate actually applied, whichever mode is in force. */
  rate: number;
  projection: Projection | null;
}

/**
 * One area's scenario: its recorded prices, its own growth trend, and the
 * projection implied by a given set of assumptions.
 *
 * The parameters are passed in rather than read from the store so a saved
 * scenario in the list can price itself without becoming the active one.
 *
 * The query key matches the one the price chart uses, so opening an area in
 * the detail panel and running any number of scenarios on it share one fetch.
 */
export function useAreaScenario(
  area: ScenarioArea | null,
  params: ScenarioParams,
): AreaScenario {
  const { surface, horizon, growthMode, customRate } = params;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["timeseries", area?.scope, area?.code],
    queryFn: () => fetchTimeSeries(area!.scope, area!.code),
    enabled: area !== null,
    staleTime: Infinity,
  });

  const years = useMemo(() => data?.years ?? [], [data]);
  const fit = useMemo(() => fitGrowth(years), [years]);
  const rate = growthMode === "custom" ? customRate : (fit?.rate ?? 0);
  const projection = useMemo(
    () => project(years, surface, rate, horizon),
    [years, surface, rate, horizon],
  );

  return { isLoading: area !== null && isLoading, isError, fit, rate, projection };
}
